import { useQuery as useApolloQuery } from '@apollo/client';
import type { GetNotificationPreferencesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetNotificationPreferencesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useErrorNotification } from '@/hooks/useErrorNotification';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Flag-gated read for the per-user notification-settings modal.
 *
 * The legacy `getNotificationPreferences` GraphQL query combined TWO Hasura
 * actions in one round trip — `notificationPreferences` (the Knock user
 * preference set) and `slackNotificationConnection` (`{ connected }`). The tRPC
 * v3 migration (RSP-6497) splits these into two cleaner procedures
 * (`notifications.userPreferences.get` + `notifications.slack.getConnection`),
 * so this hook fans out to both and reassembles the combined
 * `GetNotificationPreferencesQuery` shape the modal already consumes.
 *
 * The `createQueryHook` factory only wraps a single tRPC procedure, so this
 * two-procedure combine is hand-written but follows the same contract:
 * flag-switch between tRPC and Apollo, and map the tRPC output to the GraphQL
 * result shape.
 */
export const useGetNotificationPreferences = (): {
  loading: boolean;
  data: GetNotificationPreferencesQuery | undefined;
  refetch: () => Promise<unknown>;
  error: unknown;
} => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();

  const {
    data: preferencesData,
    isLoading: preferencesLoading,
    error: preferencesError,
    refetch: refetchPreferences,
  } = useQuery({
    ...trpc.frontend.notifications.userPreferences.get.queryOptions(),
    enabled: trpcEnabled,
  });

  const {
    data: slackData,
    isLoading: slackLoading,
    error: slackError,
    refetch: refetchSlack,
  } = useQuery({
    ...trpc.frontend.notifications.slack.getConnection.queryOptions(),
    enabled: trpcEnabled,
  });

  const trpcError = preferencesError ?? slackError;
  useErrorNotification(trpcError, trpcEnabled);

  const mappedTrpcData: GetNotificationPreferencesQuery | undefined =
    useMemo(() => {
      if (!preferencesData || !slackData) {
        return undefined;
      }

      return {
        __typename: 'query_root',
        notificationPreferences: {
          __typename: 'NotificationPreferencesOutput',
          id: preferencesData.id,
          channel_types: preferencesData.channel_types,
          categories: preferencesData.categories,
          workflows: preferencesData.workflows,
        },
        slackNotificationConnection: {
          __typename: 'SlackNotificationConnectionOutput',
          connected: slackData.connected,
        },
      };
    }, [preferencesData, slackData]);

  const {
    data: graphqlData,
    loading: graphqlLoading,
    error: graphqlError,
    refetch: refetchGraphql,
  } = useApolloQuery(GetNotificationPreferencesDocument, {
    fetchPolicy: 'no-cache',
    skip: trpcEnabled,
  });

  useErrorNotification(graphqlError, !trpcEnabled);

  const refetch = useCallback(async () => {
    if (trpcEnabled) {
      return Promise.all([refetchPreferences(), refetchSlack()]);
    }

    return refetchGraphql();
  }, [trpcEnabled, refetchPreferences, refetchSlack, refetchGraphql]);

  if (trpcEnabled) {
    return {
      loading: preferencesLoading || slackLoading,
      data: mappedTrpcData,
      refetch,
      error: trpcError,
    };
  }

  return {
    loading: graphqlLoading,
    data: graphqlData,
    refetch,
    error: graphqlError,
  };
};
